
import { useEffect } from 'react';

// Cache durations in milliseconds
export const CACHE_DURATIONS = {
  SHORT: 5 * 60 * 1000, // 5 minutes
  MEDIUM: 30 * 60 * 1000, // 30 minutes
  LONG: 24 * 60 * 60 * 1000, // 24 hours
};

// Local storage cache implementation
export const localStorageCache = {
  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      
      const parsed = JSON.parse(item);
      
      // Check if cache has expired
      if (parsed.expiry && parsed.expiry < Date.now()) {
        localStorage.removeItem(key);
        return null;
      }
      
      return parsed.data as T;
    } catch (error) {
      console.error('Error retrieving from cache:', error);
      return null;
    }
  },
  
  set: <T>(key: string, data: T, duration = CACHE_DURATIONS.MEDIUM): void => {
    try {
      const item = {
        data,
        expiry: Date.now() + duration,
      };
      localStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  },
  
  remove: (key: string): void => {
    try {
      localStorage.setItem(key, '');
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from cache:', error);
    }
  },
  
  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
};

// Cache key generator helper
export const generateCacheKey = (
  prefix: string,
  params: Record<string, any> = {}
): string => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: params[key],
      }),
      {}
    );

  return `${prefix}:${JSON.stringify(sortedParams)}`;
};

// Hook for using cached data with expiry
export const useCachedData = <T>(
  key: string,
  fetchFunction: () => Promise<T>,
  duration = CACHE_DURATIONS.MEDIUM,
  dependencies: any[] = []
): {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<T>;
} => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async (): Promise<T> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check cache first
      const cachedData = localStorageCache.get<T>(key);
      if (cachedData) {
        setData(cachedData);
        setIsLoading(false);
        return cachedData;
      }
      
      // Fetch fresh data
      const freshData = await fetchFunction();
      
      // Update cache
      localStorageCache.set(key, freshData, duration);
      
      // Update state
      setData(freshData);
      return freshData;
    } catch (error: any) {
      setError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch and refetch on dependencies change
  useEffect(() => {
    fetchData().catch(console.error);
  }, [...dependencies]);

  return { data, isLoading, error, refetch: fetchData };
};

// Missing useState import
import { useState } from 'react';
